import { useEffect, useState } from 'react';
import { Button, Col, Container, Dropdown, DropdownButton, Form, InputGroup, Row } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTransLog } from '../contexts/TransLogContext';
import useToken from '../hooks/useToken';
import useApi from '../hooks/useApi';
import { createHttpClient } from '../libs/HttpRequest';
import { PAGE_SIZE, DURATION_DISABLE } from '../models/global';
import { SapTransLog } from '../models/SapTransLog';
import TransLogDetailModal from './TransLogDetailModal';
import { DataListTable, HeadCell } from './DataListTable';
import { SapTransDuration, SapTransLogQuery } from '../models/SapTransLogQuery';
import { ConvertToUTC } from '../libs/utilities';
import { AuthenticatedTemplate } from '@azure/msal-react';
import LoadingState from './LoadingState';

function TransLogSearch() {
    let startTime = new Date(), endTime = new Date();
    startTime.setHours(0, 0, 0);
    endTime.setHours(23, 59, 59);
    const [webId, setWebId] = useState(null as any as string);
    const [userName, setUserName] = useState('');
    const [transName, setTransName] = useState('');
    const [appName, setAppName] = useState('');
    const [startDate, setStartDate] = useState(startTime);
    const [endDate, setEndDate] = useState(endTime);
    const [companyKey, setCompanyKey] = useState('');
    const [isSuccess, setIsSuccess] = useState(null as any as string);
    const [durationActionFirst, setDurationActionFirst] = useState(DURATION_DISABLE);
    const [durationValueFirst, setDurationValueFirst] = useState('');
    const [durationActionSec, setDurationActionSec] = useState(DURATION_DISABLE);
    const [durationValueSec, setDurationValueSec] = useState('');
    const [durationOperation, setDurationOperation] = useState('and');

    const { searchTransLogs, downloadTransLogs, searchStatistics, searching, exporting } = useTransLog();
    const [getToken, tokenState] = useToken();
    
    // Use the new useApi hook for transaction types and apps
    const transactionTypesApi = useApi<string[]>(`${import.meta.env.VITE_BACKEND_API_URL}/translog/trxtypes`, 'GET', false);
    const appsApi = useApi<string[]>(`${import.meta.env.VITE_BACKEND_API_URL}/translog/apps`, 'GET', false);

    const { logs, TransLogDetail } = useTransLog();
    const [show, setShow] = useState(false);

    const columns: HeadCell[] = [
        { id: 'id', label: 'ID' },
        { id: 'insertedBy', label: 'Name', valueGetter: (row: any) => `${row.insertedBy} - ${row.webId}` },
        { id: 'applicationName', label: 'App' },
        { id: 'companyKey', label: 'Shipto#' },
        { id: 'transactionType', label: 'Trans Type' },
        { id: 'transactionDate', label: 'Trans Date' },
        { id: 'transactionDuration', label: 'Duration' },
        { id: 'isSuccess', label: 'Success', valueGetter: (row: any) => row.isSuccess ? 'Yes' : 'No' }
    ];

    const handleClose = () => {
        setShow(false);
    };

    const openDetail = (log: SapTransLog) => {
        TransLogDetail(log);
        setShow(true);
    };

    const createRequest = () => {
        var request = {
            webId: webId,
            userName: userName,
            transName: transName,
            appName: appName,
            startDate: ConvertToUTC(startDate),
            endDate: ConvertToUTC(endDate),
            pageSize: PAGE_SIZE,
            pageIndex: 0,
            companyKey: companyKey,
            isSuccess: isSuccess,
            duration: [] as SapTransDuration[],
            durationOperation: durationOperation
        } as SapTransLogQuery;

        if (durationActionFirst !== DURATION_DISABLE && durationValueFirst !== '') {
            request.duration.push({
                action: durationActionFirst,
                value: durationValueFirst
            } as SapTransDuration);
        }

        if (durationActionSec !== DURATION_DISABLE && durationValueSec !== '') {
            request.duration.push({
                action: durationActionSec,
                value: durationValueSec
            } as SapTransDuration);
        }

        return request;
    }

    const searchLogs = (e: any) => {
        e.preventDefault();
        searchTransLogs(createRequest());
    };

    const exportToFile = async () => {
        var data = await downloadTransLogs(createRequest());
        let link = document.createElement('a');
        var blob = new Blob(["\uFEFF" + data], { type: 'text/csv;charset=utf-8' });
        link.href = window.URL.createObjectURL(blob);
        link.download = 'TransactionLog.csv';
        link.target = '_blank';
        link.click();
        window.URL.revokeObjectURL(link.href);
    }

    // Load initial data when authentication is ready
    useEffect(() => {
        if (tokenState.isAuthenticated && !tokenState.isLoading) {
            transactionTypesApi.makeRequest();
            appsApi.makeRequest();
        }
    }, [tokenState.isAuthenticated, tokenState.isLoading]);

    return (<>
        <AuthenticatedTemplate>
            <LoadingState 
                loading={tokenState.isLoading} 
                error={tokenState.error || transactionTypesApi.error || appsApi.error}
                loadingMessage="Initializing application..."
            >
                <Container fluid className="mt-3 layout-container">
                <Form onSubmit={searchLogs}>
                    <Row>
                        <Col md={6}>
                            <Form.Group as={Row} className="mb-3" controlId="form.userData">
                                <Col md={4}><Form.Label>WebId:</Form.Label></Col>
                                <Col md={8}>
                                    <Form.Control size="sm" type="number"
                                        placeholder="Input WebId here..."
                                        onChange={(e) => { setWebId(e.target.value); }} />
                                </Col>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group as={Row} className="mb-3" controlId="form.userName">
                                <Col md={4}><Form.Label>User Name:</Form.Label></Col>
                                <Col md={8}>
                                    <Form.Control size="sm" type="text"
                                        placeholder="Input User Name here..."
                                        onChange={(e) => { setUserName(e.target.value); }} />
                                </Col>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group as={Row} className="mb-3" controlId="form.startDate">
                                <Col md={4}><Form.Label>Start Date:</Form.Label></Col>
                                <Col md={8}>
                                    <DatePicker
                                        showTimeInput
                                        timeInputLabel="Time:"
                                        dateFormat="yyyy-MM-dd HH:mm aa"
                                        selected={startDate}
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                        onChange={(date: Date) => setStartDate(date)} />
                                </Col>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group as={Row} className="mb-3" controlId="form.endDate">
                                <Col md={4}><Form.Label>End Date:</Form.Label></Col>
                                <Col md={8}>
                                    <DatePicker
                                        showTimeInput
                                        timeInputLabel="Time:"
                                        dateFormat="yyyy-MM-dd HH:mm aa"
                                        selected={endDate}
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                        onChange={(date: Date) => setEndDate(date)} />
                                </Col>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group as={Row} className="mb-3" controlId="form.appName">
                                <Col md={4}><Form.Label>Application:</Form.Label></Col>
                                <Col md={8}>
                                    <select style={{ width: '100%' }} onChange={(e) => { setAppName(e.target.value); }}>
                                        <option value="">All</option>
                                        {(appsApi.data || []).map((i: string) => <option value={i} key={i}>{i}</option>)}
                                    </select>
                                </Col>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group as={Row} className="mb-3" controlId="form.transName">
                                <Col md={4}><Form.Label>Transacion Name:</Form.Label></Col>
                                <Col md={8}>
                                    <select style={{ width: '100%' }} onChange={(e) => { setTransName(e.target.value); }}>
                                        <option value="">All</option>
                                        {(transactionTypesApi.data || []).map((t: string) => <option value={t} key={t}>{t}</option>)}
                                    </select>
                                </Col>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group as={Row} className="mb-3" controlId="form.companyKey">
                                <Col md={4}><Form.Label>Shipto#:</Form.Label></Col>
                                <Col md={8}>
                                    <Form.Control size="sm" type="text"
                                        placeholder="Input Shipto# here..."
                                        onChange={(e) => { setCompanyKey(e.target.value); }} />
                                </Col>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group as={Row} className="mb-3" controlId="form.isSuccess">
                                <Col md={4}><Form.Label>Success:</Form.Label></Col>
                                <Col md={8}>
                                    <select style={{ width: '100%' }} onChange={(e) => { setIsSuccess(e.target.value); }}>
                                        <option value="">All</option>
                                        <option value={"1"} key={"yes"}>Yes</option>
                                        <option value={"0"} key={"no"}>No</option>
                                    </select>
                                </Col>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group as={Row} className="mb-3">
                                <Col md={4}><Form.Label>Duration:</Form.Label></Col>
                                <Col md={8}>
                                    <Row>
                                        <Col md={5}>
                                            <InputGroup size="sm" className="mb-3">
                                                <DropdownButton variant="outline-secondary" title={durationActionFirst} onSelect={(key, e: any) => setDurationActionFirst(e.target.text)}>
                                                    <Dropdown.Item>{DURATION_DISABLE}</Dropdown.Item>
                                                    <Dropdown.Item>{">"}</Dropdown.Item>
                                                    <Dropdown.Item>{"<"}</Dropdown.Item>
                                                </DropdownButton>
                                                <Form.Control disabled={durationActionFirst === DURATION_DISABLE} type='number' id="input-group-dropdown-input-1" onChange={(e) => setDurationValueFirst(e.target.value)} />
                                            </InputGroup>
                                        </Col>
                                        <Col md={2} style={{ padding: 0 }}>
                                            <select className='mt-1' style={{ width: '100%' }} onChange={(e) => setDurationOperation(e.target.value)}>
                                                <option value={"And"} key={"And"}>And</option>
                                                <option value={"Or"} key={"Or"}>Or</option>
                                            </select>
                                        </Col>
                                        <Col md={5}>
                                            <InputGroup size="sm" className="mb-3">
                                                <DropdownButton variant="outline-secondary" title={durationActionSec} onSelect={(key, e: any) => setDurationActionSec(e.target.text)}>
                                                    <Dropdown.Item>{DURATION_DISABLE}</Dropdown.Item>
                                                    <Dropdown.Item>{">"}</Dropdown.Item>
                                                    <Dropdown.Item >{"<"}</Dropdown.Item>
                                                </DropdownButton>
                                                <Form.Control disabled={durationActionSec === DURATION_DISABLE} type='number' id="input-group-dropdown-input-1" onChange={(e) => setDurationValueSec(e.target.value)} />
                                            </InputGroup>
                                        </Col>
                                    </Row>
                                </Col>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="justify-content-end">
                        <Col md={3}>
                            <Button className="ap-green w-100" disabled={logs.length === 0 || exporting} onClick={exportToFile}>Export to File</Button>
                        </Col>
                        <Col md={3}>
                            <Button type="submit" disabled={searching} className="ap-green w-100">{searching ? 'Searching...' : 'Search'}</Button>
                        </Col>
                    </Row>
                </Form >
            </Container >
            <Container fluid className="mt-3 mb-3 layout-container">
                <DataListTable clickableRowIds={['id']} headCells={columns} dataSource={logs} onRowClick={openDetail} />
            </Container>
            <TransLogDetailModal show={show} handleClose={handleClose}></TransLogDetailModal>
            </LoadingState>
        </AuthenticatedTemplate>
    </>);
}

export default TransLogSearch;